import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { authApi } from '@/api/endpoints/auth';
import { humanizeError, isApiError, rateLimitMessage } from '@/api/errors';
import { track } from '@/analytics/tracker';
import { clearInitialAuthTab, readInitialAuthTab, type AuthTab } from '@/app/urlActions';
import { toAuthErrorType, useAuth } from '@/auth/AuthProvider';
import { Logo } from '@/components/ui/Logo';
import { appConfig } from '@/config/app';
import { useT } from '@/i18n/LocaleProvider';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import type { UiKey } from '@/i18n/dictionary';
import { cx } from '@/lib/cx';
import styles from './AuthScreen.module.css';
import { CodeInput } from './CodeInput';
import { GoogleButton, isGoogleSignInEnabled } from './GoogleButton';
import { PasswordField } from './PasswordField';
import { isCompleteCode, isValidEmail, isValidPassword, normalizeCode } from './validation';

type View = 'form' | 'verify' | 'forgot';
type ForgotStep = 1 | 2 | 3;

/* Тексты — словарь i18n (auth.*); ошибки API — humanizeError / сообщения бэкенда. */
const FORGOT_TITLES: readonly UiKey[] = [
  'auth.forgotTitle1',
  'auth.forgotTitle2',
  'auth.forgotTitle3',
];

interface Props {
  /** verify — есть токен, но email не подтверждён (AUTH-11). */
  mode: 'login' | 'verify';
}

/** Единый экран авторизации (AUTH-12): вход, регистрация, код, восстановление, Google. */
export function AuthScreen({ mode }: Props) {
  const t = useT();
  const auth = useAuth();
  const { user, pendingVerificationEmail } = auth;

  // Инициализатор чистый: в StrictMode он вызывается дважды, поэтому флаг чистится в эффекте ниже.
  const [initial] = useState(() => {
    const fromUrl = readInitialAuthTab();
    return { tab: fromUrl ?? ('login' as AuthTab), source: fromUrl ? 'url_action' : 'gate' };
  });
  const [tab, setTab] = useState<AuthTab>(initial.tab);
  const [view, setView] = useState<View>(() =>
    mode === 'verify' || pendingVerificationEmail ? 'verify' : 'form',
  );

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);

  const [forgotStep, setForgotStep] = useState<ForgotStep>(1);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [forgotError, setForgotError] = useState<string | null>(null);

  const verifyEmailAddress =
    pendingVerificationEmail ?? (mode === 'verify' ? (user?.email ?? '') : email);

  useEffect(() => {
    clearInitialAuthTab();
    track('auth_screen_shown', { metadata: { tab: initial.tab, source: initial.source } });
  }, [initial]);

  // Провайдер перевёл на шаг кода (регистрация или 403 на входе).
  const lastPending = useRef<string | null>(null);
  useEffect(() => {
    // Реагируем на переход null → email, а не на само значение: после «Back» тот же
    // адрес может прийти снова (403 на входе) и обязан снова открыть шаг кода.
    const previous = lastPending.current;
    lastPending.current = pendingVerificationEmail;
    if (pendingVerificationEmail && pendingVerificationEmail !== previous) {
      setCode('');
      setCodeError(null);
      setResent(false);
      setView('verify');
    }
  }, [pendingVerificationEmail]);

  useEffect(() => {
    if (view !== 'verify') return;
    const timer = setTimeout(() => codeRef.current?.focus(), 60);
    return () => clearTimeout(timer);
  }, [view]);

  const switchTab = (next: AuthTab) => {
    setTab(next);
    setError(null);
  };

  const backToLogin = useCallback(() => {
    auth.cancelVerification();
    setView('form');
    setTab('login');
    setError(null);
    setPassword('');
    setCode('');
    setCodeError(null);
    setForgotStep(1);
    setResetCode('');
    setNewPassword('');
    setForgotError(null);
  }, [auth]);

  const submitForm = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const trimmedEmail = email.trim();
    if (!isValidEmail(trimmedEmail)) return setError(t('auth.invalidEmail'));

    if (tab === 'login') {
      if (!isValidPassword(password)) return setError(t('auth.invalidCredentials'));
      setBusy(true);
      try {
        await auth.login(trimmedEmail, password);
      } catch (err) {
        if (isApiError(err) && err.status === 403) {
          // Аккаунт есть, email не подтверждён — сразу шаг кода (AUTH-11).
          await auth.beginVerification(trimmedEmail);
        } else if (isApiError(err) && (err.status === 429 || err.status === 0)) {
          setError(humanizeError(err));
        } else {
          setError(t('auth.invalidCredentials'));
        }
      } finally {
        setBusy(false);
      }
      return;
    }

    if (!isValidPassword(password)) return setError(t('auth.passwordTooShort'));
    setBusy(true);
    try {
      await auth.register(name.trim(), trimmedEmail, password);
    } catch (err) {
      setError(humanizeError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (event: FormEvent) => {
    event.preventDefault();
    if (!isCompleteCode(code)) {
      setCodeError(t('auth.codeIncomplete'));
      codeRef.current?.focus();
      return;
    }
    setBusy(true);
    setCodeError(null);
    try {
      await auth.verifyEmail(code);
    } catch (err) {
      setCodeError(humanizeError(err));
      codeRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setCodeError(null);
    try {
      await auth.resendVerificationCode();
      setResent(true);
    } catch (err) {
      setCodeError(humanizeError(err));
    }
  };

  const submitForgot = async (event: FormEvent) => {
    event.preventDefault();
    setForgotError(null);
    const trimmedEmail = email.trim();

    if (forgotStep === 1) {
      if (!isValidEmail(trimmedEmail)) return setForgotError(t('auth.invalidEmail'));
      setBusy(true);
      try {
        // Ответ одинаков для любого адреса — существование email не раскрывается (AUTH-6).
        await authApi.requestPasswordReset(trimmedEmail);
        track('password_reset_requested', { metadata: { step: 1 } });
        setForgotStep(2);
      } catch (err) {
        track('password_reset_failed', { metadata: { step: 1, error_type: toAuthErrorType(err) } });
        setForgotError(humanizeError(err));
      } finally {
        setBusy(false);
      }
      return;
    }

    if (forgotStep === 2) {
      if (!isCompleteCode(resetCode)) return setForgotError(t('auth.codeIncomplete'));
      if (!isValidPassword(newPassword)) return setForgotError(t('auth.passwordTooShort'));
      setBusy(true);
      try {
        await authApi.resetPassword(trimmedEmail, resetCode, newPassword);
        track('password_reset_confirmed', { metadata: { step: 2 } });
        setForgotStep(3);
      } catch (err) {
        track('password_reset_failed', { metadata: { step: 2, error_type: toAuthErrorType(err) } });
        setForgotError(
          isApiError(err) && err.status === 429 ? rateLimitMessage() : humanizeError(err),
        );
      } finally {
        setBusy(false);
      }
    }
  };

  const onGoogleCredential = async (idToken: string) => {
    setError(null);
    setBusy(true);
    try {
      await auth.googleSignIn(idToken);
    } catch (err) {
      setError(humanizeError(err));
    } finally {
      setBusy(false);
    }
  };

  const googleEnabled = isGoogleSignInEnabled();
  const isLogin = tab === 'login';
  useDocumentTitle(
    mode === 'verify' ? t('auth.verifyTitle') : isLogin ? t('auth.login') : t('auth.register'),
  );

  return (
    <div className={styles.screen}>
      <div className={styles.column}>
        <div className={styles.brand}>
          <Logo className={styles.brandLogo} />
        </div>

        <div className={styles.card} role="dialog" aria-labelledby="auth-title">
          {view === 'form' ? (
            <form onSubmit={submitForm} noValidate>
              <div className={styles.tabs} role="tablist" aria-label="Sign in or create account">
                <button
                  type="button"
                  role="tab"
                  aria-selected={isLogin}
                  className={cx(styles.tab, isLogin && styles.tabActive)}
                  onClick={() => switchTab('login')}
                >
                  {t('auth.login')}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={!isLogin}
                  className={cx(styles.tab, !isLogin && styles.tabActive)}
                  onClick={() => switchTab('register')}
                >
                  {t('auth.register')}
                </button>
              </div>
              <h1 id="auth-title" hidden>
                {isLogin ? t('auth.login') : t('auth.register')}
              </h1>

              {googleEnabled ? (
                <>
                  <GoogleButton
                    text={isLogin ? 'continue_with' : 'signup_with'}
                    onCredential={onGoogleCredential}
                    onUnavailable={() => setError(humanizeError(null))}
                    disabled={busy}
                  />
                  <div className={styles.divider} aria-hidden="true">
                    {t('common.or')}
                  </div>
                </>
              ) : null}

              {!isLogin ? (
                <label className={styles.field} htmlFor="auth-name">
                  <span className={styles.label}>{t('auth.name')}</span>
                  <input
                    id="auth-name"
                    className={styles.input}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('auth.namePlaceholder')}
                    autoComplete="name"
                    data-clarity-mask="true"
                    disabled={busy}
                  />
                </label>
              ) : null}

              <label className={styles.field} htmlFor="auth-email">
                <span className={styles.label}>{t('auth.email')}</span>
                <input
                  id="auth-email"
                  type="email"
                  className={styles.input}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  autoComplete="email"
                  inputMode="email"
                  data-clarity-mask="true"
                  disabled={busy}
                />
              </label>

              <PasswordField
                id="auth-password"
                label={t('auth.password')}
                hint={isLogin ? undefined : t('auth.passwordHint')}
                value={password}
                onChange={setPassword}
                error={!!error}
                autoComplete={isLogin ? 'current-password' : 'new-password'}
                className={styles.fieldTight}
                disabled={busy}
              />

              {error ? (
                <p className={styles.error} role="alert">
                  {error}
                </p>
              ) : null}

              {isLogin ? (
                <button
                  type="button"
                  className={styles.forgot}
                  onClick={() => {
                    setView('forgot');
                    setForgotStep(1);
                    setForgotError(null);
                  }}
                >
                  {t('auth.forgot')}
                </button>
              ) : null}

              <button
                type="submit"
                className={cx(styles.submit, !isLogin && styles.submitSpaced)}
                disabled={busy}
              >
                {isLogin ? t('auth.login') : t('auth.register')}
              </button>

              {!isLogin ? (
                <p className={styles.terms}>
                  {t('auth.terms')}
                  <a href={appConfig.termsUrl} target="_blank" rel="noopener noreferrer">
                    {t('auth.termsLink')}
                  </a>
                  .
                </p>
              ) : null}
            </form>
          ) : null}

          {view === 'verify' ? (
            <form onSubmit={submitCode} noValidate>
              <p className={styles.eyebrow}>{t('auth.verifyEyebrow')}</p>
              <h1 id="auth-title" className={styles.title}>
                {t('auth.verifyTitle')}
              </h1>
              <p className={styles.lead}>
                {t('auth.verifyLeadBefore')}
                <span className={styles.leadEmail} data-clarity-mask="true">
                  {verifyEmailAddress}
                </span>
                .
              </p>
              <CodeInput
                ref={codeRef}
                id="auth-code"
                value={code}
                onChange={(next) => {
                  setCode(next);
                  setCodeError(null);
                }}
                error={!!codeError}
                describedBy={codeError ? 'auth-code-error' : undefined}
                disabled={busy}
              />
              {codeError ? (
                <p id="auth-code-error" className={styles.codeError} role="alert">
                  {codeError}
                </p>
              ) : null}
              <button type="submit" className={styles.submit} disabled={busy}>
                {t('common.confirm')}
              </button>
              <div className={styles.row}>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={resend}
                  disabled={busy}
                >
                  {resent ? t('auth.sentAgain') : t('auth.sendAgain')}
                </button>
                <button
                  type="button"
                  className={cx(styles.linkButton, styles.linkMuted)}
                  onClick={() => (mode === 'verify' ? auth.logout('manual') : backToLogin())}
                >
                  {t('common.back')}
                </button>
              </div>
            </form>
          ) : null}

          {view === 'forgot' ? (
            <form onSubmit={submitForgot} noValidate>
              <p className={styles.eyebrow}>{t('auth.step', { n: forgotStep })}</p>
              <h1 id="auth-title" className={cx(styles.title, styles.titleSpaced)}>
                {t(FORGOT_TITLES[forgotStep - 1]!)}
              </h1>

              {forgotStep === 1 ? (
                <>
                  <label className={cx(styles.field, styles.fieldLast)} htmlFor="forgot-email">
                    <span className={styles.label}>{t('auth.email')}</span>
                    <input
                      id="forgot-email"
                      type="email"
                      className={cx(styles.input, forgotError && styles.inputError)}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      inputMode="email"
                      data-clarity-mask="true"
                      disabled={busy}
                    />
                  </label>
                  {forgotError ? (
                    <p className={styles.error} role="alert">
                      {forgotError}
                    </p>
                  ) : null}
                  <button type="submit" className={styles.submit} disabled={busy}>
                    {t('auth.sendCode')}
                  </button>
                  <p className={styles.note}>{t('auth.forgotNote')}</p>
                </>
              ) : null}

              {forgotStep === 2 ? (
                <>
                  <label className={styles.field} htmlFor="forgot-code">
                    <span className={styles.label}>{t('auth.codeFromEmail')}</span>
                    <input
                      id="forgot-code"
                      className={cx(styles.input, styles.inputMono)}
                      value={resetCode}
                      onChange={(e) => setResetCode(normalizeCode(e.target.value))}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="one-time-code"
                      placeholder={t('auth.codePlaceholder')}
                      disabled={busy}
                    />
                  </label>
                  <PasswordField
                    id="forgot-password"
                    label={t('auth.newPassword')}
                    value={newPassword}
                    onChange={setNewPassword}
                    autoComplete="new-password"
                    className={styles.fieldLast}
                    disabled={busy}
                  />
                  {forgotError ? (
                    <p className={styles.error} role="alert">
                      {forgotError}
                    </p>
                  ) : null}
                  <button type="submit" className={styles.submit} disabled={busy}>
                    {t('auth.setPassword')}
                  </button>
                </>
              ) : null}

              {forgotStep === 3 ? (
                <>
                  <p className={styles.lead}>{t('auth.resetDone')}</p>
                  <button type="button" className={styles.submit} onClick={backToLogin}>
                    {t('auth.backToLogin')}
                  </button>
                </>
              ) : (
                <button type="button" className={styles.backLink} onClick={backToLogin}>
                  {t('auth.backToLogin')}
                </button>
              )}
            </form>
          ) : null}
        </div>

        {appConfig.apiMode === 'mock' ? (
          <p className={styles.footnote}>{t('auth.mockNote')}</p>
        ) : null}
      </div>
    </div>
  );
}
